import { useEffect, useMemo } from 'react'

import { BuilderPage } from '@makyoapp/makyo-common'

import { IAppLayer } from '@/core/app-builder/app/layer'
import { IAppPage } from '@/core/app-builder/app/page'
import constate from '@/lib/constate'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { BuilderOverlay } from '@/shared/overlay'

import { useAppOverlaysContext } from '../AppOverlaysContext'
import { useAppPagesContext } from '../AppPagesContext'
import { useAppRoutingContext } from './AppRoutingContext'
import { useDbClientContext } from './DbClientContext'

/**
 * Use active layer context to manage editing current active page in editor or navigator
 */
const [ActiveLayerProvider, useActiveLayerContext] = constate('ActiveLayerContext', () => {
  const { currentRowId } = useAppRoutingContext()

  const { appClient, activeLayer, setActiveLayer, activeComponent, setActiveComponent } = useAppBuilderContext()
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { activePage, activePageIndex } = useAppPagesContext()
  const { activeOverlays } = useAppOverlaysContext()

  const activeLayerId = activeLayer.id

  useEffect(() => {
    if (activeOverlays.length) {
      let lastOverlay = activeOverlays[activeOverlays.length - 1]
      let activeOverlayId = lastOverlay.id
      setActiveLayer({
        type: 'overlay',
        id: activeOverlayId,
        index: 0,
        tableId: lastOverlay.tableId,
      })
    } else if (activePage && activePageIndex > -1) {
      setActiveLayer({
        type: 'page',
        id: activePage.id,
        index: activePageIndex,
        tableId: activePage.tableId,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOverlays, activePage, activePageIndex])

  // activeLayerCient is essential to get the actual layer (page/overlay)
  const activeLayerClient: IAppLayer | undefined = useMemo(() => {
    // if the layer has tableID, its techincally a page
    if (activeLayer.type === 'overlay' && !activeLayer.tableId) {
      return appClient.overlaysMap.get(activeLayerId)
    }
    return appClient.pagesMap.get(activeLayerId)
  }, [activeLayer.tableId, activeLayer.type, activeLayerId, appClient.overlaysMap, appClient.pagesMap])

  const activeLayerContainer = useMemo(() => {
    let container = activeLayer.type === 'overlay' ? activeOverlays[activeOverlays.length - 1] : activePage
    return container as BuilderPage | BuilderOverlay
  }, [activeLayer.type, activeOverlays, activePage])

  const activeLayerElements = activeLayerContainer?.elements || []
  const activeLayerElementSettings = (activeLayerContainer?.elementSettings || {}) as Record<string, any>

  const activeLayerTableId = activeLayerContainer?.tableId
  const isDetailPage = !!activeLayerTableId

  const activeLayerDetailRowId = useMemo(() => {
    if (activeLayer.type === 'page') {
      return currentRowId
    }
    if (activeLayer.type === 'overlay') {
      return activeOverlays[activeOverlays.length - 1]?.rowId
    }
    return undefined
  }, [activeLayer.type, activeOverlays, currentRowId])

  const activeLayerDetailRowIndex = useMemo(() => {
    let rowIdx = undefined

    if (dbClient && activeLayerTableId && activeLayerDetailRowId) {
      const dbTable = dbClient.tablesMap.get(activeLayerTableId)
      if (dbTable) {
        rowIdx = dbTable.GetRowIndexByRowId(activeLayerDetailRowId, language, userSpecific)
      }
    }
    return rowIdx
    //
  }, [activeLayerDetailRowId, activeLayerTableId, dbClient, language, userSpecific])

  // page only client
  const { activePageTableId, activePageRowIndex, activePageRowId, activePageClient } = useMemo(() => {
    return {
      activePageClient: activeLayer.type === 'page' ? (activeLayerClient as IAppPage) : undefined,
      activePageTableId: activeLayer.type === 'page' ? activeLayerTableId : undefined,
      activePageRowIndex: activeLayer.type === 'page' ? activeLayerDetailRowIndex : undefined,
      activePageRowId: activeLayer.type === 'page' ? activeLayerDetailRowId : undefined,
    }
  }, [activeLayer.type, activeLayerClient, activeLayerDetailRowIndex, activeLayerDetailRowId, activeLayerTableId])

  return {
    activeComponent,
    setActiveComponent,
    activeLayer,
    activeLayerClient,
    activeLayerContainer,
    activeLayerElements,
    activeLayerElementSettings,
    isDetailPage,
    activeLayerTableId,
    activeLayerDetailRowIndex,
    activeLayerDetailRowId,

    // page specific layer
    activePageTableId,
    activePageRowIndex,
    activePageRowId,
    activePageClient,
  }
})

export { ActiveLayerProvider, useActiveLayerContext }
